<template>
  <div :class="$style.wrapper">
    <div v-if="isLoading && notifications.length === 0" :class="$style.loading">
      Загрузка...
    </div>

    <div v-else-if="notifications.length === 0" :class="$style.empty">
      Уведомлений нет
    </div>

    <div v-else :class="$style.list">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          $style.notificationItem,
          !notification.is_read && $style.unread,
        ]"
        @click="handleNotificationClick(notification)"
      >
        <div :class="$style.notificationContent">
          <div :class="$style.title">
            <span
              v-if="!notification.is_read"
              :class="$style.unreadDot"
              aria-hidden="true"
            />
            {{ notification.title }}
          </div>
          <div v-if="notification.message" :class="$style.message">
            {{ notification.message }}
          </div>
          <div :class="$style.time">
            {{ formatDate(notification.created_at) }}
          </div>
        </div>
      </div>
      <div v-if="isLoadingMore" :class="$style.loadingMore">
        <div :class="$style.loadingSpinner"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Notifications",
  data() {
    return {
      notifications: [],
      isLoading: true,
      isLoadingMore: false,
      hasMore: true,
      ws: null,
      limit: 20,
      offset: 0,
    }
  },
  async mounted() {
    await this.fetchNotifications(true)
    this.connectWebSocket()
    window.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    this.disconnectWebSocket()
    window.removeEventListener("scroll", this.handleScroll)
    this.disconnectWebSocket()
  },
  methods: {
    async fetchNotifications(reset = false) {
      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
      } else {
        if (!this.hasMore || this.isLoadingMore) return
        this.isLoadingMore = true
      }

      try {
        const response = await this.$store.dispatch("getNotifications", {
          limit: this.limit,
          offset: this.offset,
        })
        const data = response?.data || response
        const newItems = data.items || []

        if (reset) {
          this.notifications = newItems
        } else {
          this.notifications = [...this.notifications, ...newItems]
        }

        // Проверяем, есть ли еще данные
        this.hasMore = newItems.length === this.limit
        if (this.hasMore) {
          this.offset += this.limit
        }
      } catch (error) {
        console.error("Ошибка при получении уведомлений:", error)
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // Загружаем, когда осталось 200px до конца
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.fetchNotifications(false)
      }
    },
    connectWebSocket() {
      const tg = window.Telegram?.WebApp
      if (!tg?.initData) {
        console.warn("Telegram WebApp initData не доступен")
        return
      }

      // Если уже подключены, не подключаемся снова
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log("WebSocket уже подключен")
        return
      }

      // Закрываем старое соединение, если есть
      if (this.ws) {
        this.ws.close()
      }

      // Формируем URL для WebSocket
      const wsBaseUrl =
        process.env.WS_API_BASE_URL ||
        "wss://snkrroom-resell.ru/api/resell/v1/ws/notifications"
      const initData = encodeURIComponent(tg.initData)
      const wsUrl = `${wsBaseUrl}?init_data=${initData}`

      try {
        console.log(
          "Подключение к WebSocket:",
          wsUrl.replace(initData, "[REDACTED]")
        )
        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = () => {
          console.log("WebSocket подключен для уведомлений")
        }

        this.ws.onmessage = (event) => {
          try {
            console.log("Получено сообщение от WebSocket:", event.data)
            const data = JSON.parse(event.data)
            console.log("Распарсенные данные:", data)

            // Если получены новые данные от WebSocket, обновляем список уведомлений
            if (data) {
              console.log(
                "Получены новые данные от WebSocket, обновляем список уведомлений"
              )
              this.fetchNotifications(true)
            }
          } catch (error) {
            console.error("Ошибка при обработке сообщения WebSocket:", error)
            console.error("Данные:", event.data)
          }
        }

        this.ws.onerror = (error) => {
          console.error("Ошибка WebSocket:", error)
          console.error("Состояние WebSocket:", this.ws?.readyState)
        }

        this.ws.onclose = (event) => {
          console.log("WebSocket отключен", {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          })
          this.ws = null

          // Пытаемся переподключиться через 5 секунд, если это не было намеренное закрытие
          if (event.code !== 1000) {
            setTimeout(() => {
              if (this.$el && this.$el.isConnected) {
                this.connectWebSocket()
              }
            }, 5000)
          }
        }
      } catch (error) {
        console.error("Ошибка при подключении WebSocket:", error)
        this.ws = null
      }
    },
    disconnectWebSocket() {
      if (this.ws) {
        // Закрываем с кодом 1000 (нормальное закрытие)
        this.ws.close(1000, "Component unmounting")
        this.ws = null
      }
    },
    async handleNotificationClick(notification) {
      // Отмечаем как прочитанное
      await this.markAsRead(notification)

      // Если есть payload.url, переходим на него
      if (notification.payload?.url) {
        const url = notification.payload.url

        // Извлекаем ID сделки из URL
        // Формат: https://snkrroom-resell.ru/api/resell/v1/deal/197
        // или: /api/resell/v1/deal/197
        const dealIdMatch = url.match(/\/deal\/(\d+)/)
        if (dealIdMatch && dealIdMatch[1]) {
          const dealId = dealIdMatch[1]
          // Переходим на страницу сделки
          this.$router.push({
            name: "deal-details",
            params: { id: dealId },
          })
        } else {
          // Если не удалось извлечь ID, пробуем открыть как есть
          console.warn("Не удалось извлечь ID сделки из URL:", url)
        }
      }
    },
    async markAsRead(notification) {
      if (notification.is_read) return

      try {
        await this.$store.dispatch("markNotificationsAsRead", {
          ids: [notification.id],
        })
        // Обновляем локальное состояние
        notification.is_read = true
      } catch (error) {
        console.error("Ошибка при отметке уведомления как прочитанного:", error)
      }
    },
    formatDate(dateString) {
      if (!dateString) return ""

      try {
        const date = new Date(dateString)
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const notificationDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        )

        // Проверяем, сегодня ли это
        if (notificationDate.getTime() === today.getTime()) {
          const hours = String(date.getHours()).padStart(2, "0")
          const minutes = String(date.getMinutes()).padStart(2, "0")
          return `Сегодня, ${hours}:${minutes}`
        }

        // Проверяем, вчера ли это
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        if (notificationDate.getTime() === yesterday.getTime()) {
          const hours = String(date.getHours()).padStart(2, "0")
          const minutes = String(date.getMinutes()).padStart(2, "0")
          return `Вчера, ${hours}:${minutes}`
        }

        // Для других дат показываем полную дату
        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const hours = String(date.getHours()).padStart(2, "0")
        const minutes = String(date.getMinutes()).padStart(2, "0")
        return `${day}.${month}, ${hours}:${minutes}`
      } catch (error) {
        return dateString
      }
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0 1rem 6rem 1rem;
  min-height: 100vh;
  background: #fff;
}

.loading,
.empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #7b7b81;
  font-size: 1rem;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.notificationItem {
  padding: 1rem 0;
  border-bottom: 1px solid #f0f0f0aa;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }
}

.notificationContent {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.unreadDot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: #ff3b30;
  box-shadow: 0 0 0 2px #fff;
  vertical-align: middle;
}

.title {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  line-height: 1.3;
}

.message {
  font-size: 0.875rem;
  color: #666;
  line-height: 1.4;
}

.time {
  font-size: 0.875rem;
  color: #999;
  margin-top: 0.25rem;
}

.loadingMore {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
